import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { getEntityId } from 'helpers/entityId';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faExclamationTriangle,
  faSave,
  faRightFromBracket
} from '@fortawesome/free-solid-svg-icons';
import {
  Document,
  Page,
  Text,
  View,
  PDFDownloadLink,
  Image
} from '@react-pdf/renderer';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from 'react-query';
import {
  Job,
  CREATE_JOB_ACTIVITY_LOG,
  getActivityLogsByJobId
} from 'services/api/job';
import { useAccessToken } from 'providers/AccessTokenProvider';
import hocLogo from '../../../assets/img/icons/favicon-100x100.png';
import './post-job-report.css';
import { pjrPdfStyles } from './styles/pdfStyles';
import {
  PostJobReport,
  REPORT_STEPS,
  calcAV,
  calcAvgROP,
  calcBHACumulative,
  calcEquivalentHoleSize,
  calcGPM,
  calcTotalInterval,
  createDefaultReport
} from './types/types';
import Step1WellSetup from './steps/Step1WellSetup';
import Step2DrillingParams from './steps/Step2DrillingParams';
import Step3ToolCasing from './steps/Step3ToolCasing';
import Step4BHA from './steps/Step4BHA';
import Step5EngineeringLog from './steps/Step5EngineeringLog';
import Step6ReviewExport from './steps/Step6ReviewExport';

// ─── PDF Document ──────────────────────────────────────────────────────────────

const PostJobReportPDF: React.FC<{ report: PostJobReport; job: Job }> = ({
  report: r,
  job
}) => {
  const totalInterval = calcTotalInterval(r.urDepthIn, r.urDepthOut);
  const avgROP = calcAvgROP(totalInterval, r.totalDrillHours);
  const gpm = calcGPM(r.annularVelocity.pumpOutput, r.annularVelocity.totalSPM);
  const eqHoleSize = calcEquivalentHoleSize(r.openingDia, r.pilotHoleDia);
  const avDPOpenHole = calcAV(
    gpm,
    r.annularVelocity.idOfHole,
    r.annularVelocity.odOfDP
  );
  const avDCOpenHole = calcAV(
    gpm,
    r.annularVelocity.idOfHole,
    r.annularVelocity.odOfCollars
  );
  const avDPCasing = calcAV(
    gpm,
    r.annularVelocity.idOfCasing,
    r.annularVelocity.odOfDP
  );
  const avDCCasing = calcAV(
    gpm,
    r.annularVelocity.idOfCasing,
    r.annularVelocity.odOfCollars
  );
  const cumulatives = calcBHACumulative(r.bhaComponents);
  const totalBHALength = cumulatives[cumulatives.length - 1] ?? 0;
  const s = pjrPdfStyles;
  const fmt = (v: any, suffix = '') =>
    v !== '' && v !== null && v !== undefined ? `${v}${suffix}` : '—';

  const InfoRow: React.FC<{ pairs: [string, string][] }> = ({ pairs }) => (
    <View style={s.infoRow}>
      {pairs.map(([label, value], i) => (
        <View
          key={i}
          style={i < pairs.length - 1 ? s.infoCell : s.infoCellLast}
        >
          <Text style={s.infoCellLabel}>{label}</Text>
          <Text style={s.infoCellValue}>{value}</Text>
        </View>
      ))}
    </View>
  );

  return (
    <Document>
      {/* ── Page 1: Header + General Info + Drilling + Tools ─────────────────── */}
      <Page size="A4" style={s.page}>
        {/* Header */}
        <View style={s.header}>
          <View style={s.headerRow}>
            <Image src={hocLogo} style={s.logoImage} />
            <View style={s.headerTitleBlock}>
              <Text style={s.reportTitle}>Post Job Underreamer Report</Text>
              <Text style={s.reportSubtitle}>
                Customer: {job.customer?.organizationName || '—'} | Rig:{' '}
                {job.rig?.name || '—'} | Job #: {job.jobNumber || '—'}
              </Text>
            </View>
            <View>
              <Text style={s.formId}>HOC/FORMS/020</Text>
              <Text style={s.formId}>
                Generated: {new Date().toLocaleDateString()}
              </Text>
              <Text
                style={[
                  s.formId,
                  {
                    marginTop: 4,
                    fontFamily: 'Helvetica-Bold',
                    color:
                      r.reportStatus === 'submitted' ? '#198754' : '#856404'
                  }
                ]}
              >
                {r.reportStatus.toUpperCase()}
              </Text>
            </View>
          </View>
        </View>

        {/* General Info Table */}
        <Text style={s.sectionHeading}>SECTION A — GENERAL INFORMATION</Text>
        <View style={s.infoTable}>
          <InfoRow
            pairs={[
              ['Customer', job.customer?.organizationName || '—'],
              ['Contractor', r.contractor || '—'],
              [
                'HOC Supervisor',
                job.supervisor?.[0]
                  ? `${job.supervisor[0].firstName} ${job.supervisor[0].lastName}`
                  : '—'
              ],
              [
                'Company Rep',
                job.customerRepresentative
                  ? `${job.customerRepresentative.firstName} ${job.customerRepresentative.lastName}`
                  : '—'
              ]
            ]}
          />
          <InfoRow
            pairs={[
              ['Rig', job.rig?.name || '—'],
              ['Rig Type', r.rigType || '—'],
              ['Rig Location', r.rigLocation || '—'],
              ['Rotary Drive', r.rotaryDrive || '—']
            ]}
          />
          <InfoRow
            pairs={[
              ['Well', job.well || '—'],
              ['Lease', r.lease || '—'],
              ['Block', fmt(r.block)],
              [
                'Field',
                typeof job.location === 'object'
                  ? job.location?.location || '—'
                  : '—'
              ]
            ]}
          />
          <InfoRow
            pairs={[
              [
                'Start Date',
                job.departureDate
                  ? new Date(job.departureDate).toLocaleDateString()
                  : '—'
              ],
              [
                'End Date',
                job.returnDate
                  ? new Date(job.returnDate).toLocaleDateString()
                  : '—'
              ],
              ['Job Number', job.jobNumber || '—'],
              ['Type of Job', r.typeOfJob || '—']
            ]}
          />
          <InfoRow
            pairs={[
              ['Tool Type', r.toolType || '—'],
              ['Tool Series', fmt(r.toolSeries)],
              ['Pilot Hole Dia', fmt(r.pilotHoleDia, '"')],
              ['Opening Dia', fmt(r.openingDia, '"')]
            ]}
          />
          <View style={s.infoRowLast}>
            <View style={s.infoCell}>
              <Text style={s.infoCellLabel}>UR Depth In</Text>
              <Text style={s.infoCellValue}>{fmt(r.urDepthIn, ' ft')}</Text>
            </View>
            <View style={s.infoCell}>
              <Text style={s.infoCellLabel}>UR Depth Out</Text>
              <Text style={s.infoCellValue}>{fmt(r.urDepthOut, ' ft')}</Text>
            </View>
            <View style={s.infoCell}>
              <Text style={s.infoCellLabel}>Total Interval</Text>
              <Text
                style={[
                  s.infoCellValue,
                  { color: '#0d6efd', fontFamily: 'Helvetica-Bold' }
                ]}
              >
                {totalInterval !== null ? `${totalInterval} ft` : '—'}
              </Text>
            </View>
            <View style={s.infoCellLast}>
              <Text style={s.infoCellLabel}>Avg ROP</Text>
              <Text
                style={[
                  s.infoCellValue,
                  { color: '#0d6efd', fontFamily: 'Helvetica-Bold' }
                ]}
              >
                {avgROP !== null ? `${avgROP.toFixed(1)} ft/hr` : '—'}
              </Text>
            </View>
          </View>
        </View>

        {/* Run Data */}
        {r.runs.length > 0 && (
          <>
            <Text style={s.sectionHeading}>SECTION C — TOOL RUN DATA</Text>
            <View style={s.dataTable}>
              <View style={s.tableHeader}>
                {[
                  'Run#',
                  'Serial #',
                  'Depth In',
                  'Depth Out',
                  'Interval',
                  'Hrs Drill',
                  'ROP',
                  'WOB Lo-Hi',
                  'RPM Lo-Hi',
                  'Flow (gpm)',
                  'Mud Wt',
                  'PSI Across UR'
                ].map(h => (
                  <Text key={h} style={[s.tableHeaderCell, { fontSize: 6 }]}>
                    {h}
                  </Text>
                ))}
              </View>
              {r.runs.map((run, i) => {
                const ri = calcTotalInterval(run.urDepthIn, run.urDepthOut);
                const rr = calcAvgROP(ri, run.totHoursDrilling);
                return (
                  <View key={i} style={s.tableRow}>
                    {[
                      String(run.runNumber),
                      run.serialNumber || '—',
                      fmt(run.urDepthIn, "'"),
                      fmt(run.urDepthOut, "'"),
                      ri !== null ? `${ri}'` : '—',
                      fmt(run.totHoursDrilling, ' h'),
                      rr !== null ? `${rr.toFixed(1)}` : '—',
                      run.wobLo !== '' && run.wobHi !== ''
                        ? `${run.wobLo}–${run.wobHi}k`
                        : '—',
                      run.rpmLo !== '' && run.rpmHi !== ''
                        ? `${run.rpmLo}–${run.rpmHi}`
                        : '—',
                      fmt(run.flowRate),
                      fmt(run.mudWeight, ' ppg'),
                      fmt(run.psiAcrossUR, ' psi')
                    ].map((val, ci) => (
                      <Text
                        key={ci}
                        style={ci === 11 ? s.tableCellLast : s.tableCell}
                      >
                        {val}
                      </Text>
                    ))}
                  </View>
                );
              })}
            </View>
          </>
        )}

        {/* Tool & Bit Info */}
        <Text style={s.sectionHeading}>SECTION B — TOOL & BIT INFORMATION</Text>
        {r.tools.map((tool, i) => (
          <View key={i} style={[s.kvGrid, { marginBottom: 6 }]}>
            <View style={s.kvItem}>
              <Text style={s.kvLabel}>Tool #{i + 1} Serial</Text>
              <Text style={s.kvValue}>{tool.serialNumber || '—'}</Text>
            </View>
            <View style={s.kvItem}>
              <Text style={s.kvLabel}>Type</Text>
              <Text style={s.kvValue}>{tool.toolType || '—'}</Text>
            </View>
            <View style={s.kvItem}>
              <Text style={s.kvLabel}>Cutter</Text>
              <Text style={s.kvValue}>{tool.cutterType || '—'}</Text>
            </View>
            <View style={s.kvItem}>
              <Text style={s.kvLabel}>TFA</Text>
              <Text style={s.kvValue}>{fmt(tool.tfa, ' in²')}</Text>
            </View>
            <View style={s.kvItem}>
              <Text style={s.kvLabel}>B/N Jet</Text>
              <Text style={s.kvValue}>{tool.bnJet || '—'}</Text>
            </View>
            <View style={s.kvItem}>
              <Text style={s.kvLabel}>Jets Above</Text>
              <Text style={s.kvValue}>{tool.jetsAboveCutter || '—'}</Text>
            </View>
            <View style={s.kvItem}>
              <Text style={s.kvLabel}>Jets Below</Text>
              <Text style={s.kvValue}>{tool.jetsBelowCutter || '—'}</Text>
            </View>
          </View>
        ))}
        <View style={[s.kvGrid, { marginBottom: 6 }]}>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Bit Size</Text>
            <Text style={s.kvValue}>{fmt(r.bitSize, '"')}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Bit Mfg</Text>
            <Text style={s.kvValue}>{r.bitMfg || '—'}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Bit Type</Text>
            <Text style={s.kvValue}>{r.bitType || '—'}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Bit Jets</Text>
            <Text style={s.kvValue}>{r.bitJets || '—'}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Bit TFA</Text>
            <Text style={s.kvValue}>{fmt(r.bitTFA, ' in²')}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>MWD Size</Text>
            <Text style={s.kvValue}>{fmt(r.mwdSize, '"')}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>MWD Mfg</Text>
            <Text style={s.kvValue}>{r.mwdMfg || '—'}</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>
            HOC/FORMS/020 — Underreamer Report with BHA
          </Text>
          <Text
            style={s.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>

      {/* ── Page 2: BHA + Engineering + Comments ─────────────────────────────── */}
      <Page size="A4" style={s.page}>
        <Text style={s.sectionHeading}>BHA — BOTTOM HOLE ASSEMBLY</Text>
        <View style={s.dataTable}>
          <View style={s.tableHeader}>
            {[
              '#',
              'Component',
              'Mfg',
              'Serial #',
              'OD',
              'ID',
              'Top Conn',
              'Bot Conn',
              'Length (ft)',
              'Cumul (ft)'
            ].map(h => (
              <Text key={h} style={[s.tableHeaderCell, { fontSize: 6.5 }]}>
                {h}
              </Text>
            ))}
          </View>
          {r.bhaComponents.map((c, i) => (
            <View key={i} style={s.tableRow}>
              {[
                String(c.itemNumber),
                c.component || '—',
                c.mfg || '—',
                c.serialNumber || '—',
                fmt(c.od, '"'),
                fmt(c.id, '"'),
                c.topConnection || '—',
                c.bottomConnection || '—',
                c.length !== '' ? `${c.length} ft` : '—',
                `${cumulatives[i].toFixed(2)} ft`
              ].map((val, ci) => (
                <Text
                  key={ci}
                  style={
                    ci === 9
                      ? [
                          s.tableCellLast,
                          { color: '#0d6efd', fontFamily: 'Helvetica-Bold' }
                        ]
                      : s.tableCellLeft
                  }
                >
                  {val}
                </Text>
              ))}
            </View>
          ))}
          <View style={[s.tableRow, { backgroundColor: '#e8edf2' }]}>
            <Text
              style={[
                s.tableCell,
                {
                  flex: 8,
                  textAlign: 'right',
                  fontFamily: 'Helvetica-Bold',
                  fontSize: 8
                }
              ]}
            >
              Total BHA Length:
            </Text>
            <Text
              style={[
                s.tableCell,
                { flex: 2, fontFamily: 'Helvetica-Bold', color: '#0d6efd' }
              ]}
            >
              {totalBHALength.toFixed(2)} ft
            </Text>
          </View>
        </View>

        {/* Engineering */}
        <Text style={s.sectionHeading}>
          ENGINEERING — EQ. HOLE SIZE & ANNULAR VELOCITY
        </Text>
        <View style={s.kvGrid}>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Pump Output</Text>
            <Text style={s.kvValue}>
              {fmt(r.annularVelocity.pumpOutput, ' bbl/stk')}
            </Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Total SPM</Text>
            <Text style={s.kvValue}>{fmt(r.annularVelocity.totalSPM)}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Calculated GPM</Text>
            <Text
              style={[
                s.kvValue,
                { color: '#0d6efd', fontFamily: 'Helvetica-Bold' }
              ]}
            >
              {gpm !== null ? `${gpm.toFixed(0)} gpm` : '—'}
            </Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Equiv. Hole Size</Text>
            <Text
              style={[
                s.kvValue,
                { color: '#198754', fontFamily: 'Helvetica-Bold' }
              ]}
            >
              {eqHoleSize !== null ? `${eqHoleSize.toFixed(3)}"` : '—'}
            </Text>
          </View>
        </View>
        <View style={s.kvGrid}>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>AV DP × Open Hole</Text>
            <Text style={[s.kvValue, { fontFamily: 'Helvetica-Bold' }]}>
              {avDPOpenHole !== null
                ? `${avDPOpenHole.toFixed(1)} ft/min`
                : '—'}
            </Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>AV DC × Open Hole</Text>
            <Text style={[s.kvValue, { fontFamily: 'Helvetica-Bold' }]}>
              {avDCOpenHole !== null
                ? `${avDCOpenHole.toFixed(1)} ft/min`
                : '—'}
            </Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>AV DP × Casing</Text>
            <Text style={[s.kvValue, { fontFamily: 'Helvetica-Bold' }]}>
              {avDPCasing !== null ? `${avDPCasing.toFixed(1)} ft/min` : '—'}
            </Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>AV DC × Casing</Text>
            <Text style={[s.kvValue, { fontFamily: 'Helvetica-Bold' }]}>
              {avDCCasing !== null ? `${avDCCasing.toFixed(1)} ft/min` : '—'}
            </Text>
          </View>
        </View>

        {/* Daily Log */}
        {r.jobLog.length > 0 && (
          <>
            <Text style={[s.sectionHeading, { marginTop: 12 }]}>
              DAILY JOB LOG
            </Text>
            {r.jobLog.map((entry, i) => (
              <View key={i} style={s.logEntry}>
                <Text style={s.logDate}>{entry.date}</Text>
                <Text style={s.logText}>{entry.narrative || '—'}</Text>
              </View>
            ))}
          </>
        )}

        {/* Comments */}
        {r.comments && (
          <>
            <Text style={[s.sectionHeading, { marginTop: 12 }]}>
              COMMENTS & FINAL CONDITIONS
            </Text>
            <View style={s.commentsBox}>
              <Text style={s.commentsText}>{r.comments}</Text>
            </View>
          </>
        )}

        {/* Bit Grading */}
        <Text style={[s.sectionHeading, { marginTop: 12 }]}>
          SECTION D — BIT GRADING
        </Text>
        <View style={s.kvGrid}>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Inner Row</Text>
            <Text style={s.kvValue}>{r.bitGrading.innerRow}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Outer Row</Text>
            <Text style={s.kvValue}>{r.bitGrading.outerRow}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Dull Char</Text>
            <Text style={s.kvValue}>{r.bitGrading.dullChar}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Location</Text>
            <Text style={s.kvValue}>{r.bitGrading.location}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Bearing</Text>
            <Text style={s.kvValue}>{r.bitGrading.bearing}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Gauge</Text>
            <Text style={s.kvValue}>{r.bitGrading.gauge}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Other Dull Char</Text>
            <Text style={s.kvValue}>{r.bitGrading.otherDullChar}</Text>
          </View>
          <View style={s.kvItem}>
            <Text style={s.kvLabel}>Reason Pulled</Text>
            <Text style={s.kvValue}>{r.bitGrading.reasonPulled}</Text>
          </View>
        </View>

        <View style={s.footer} fixed>
          <Text style={s.footerText}>
            HOC/FORMS/020 — Underreamer Report with BHA
          </Text>
          <Text
            style={s.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

// ─── Stepper ───────────────────────────────────────────────────────────────────

const Stepper: React.FC<{
  currentStep: number;
  visitedSteps: Set<number>;
  onStepClick: (step: number) => void;
}> = ({ currentStep, visitedSteps, onStepClick }) => (
  <div className="pjr-stepper">
    {REPORT_STEPS.map((step, index) => {
      const isActive = currentStep === step.number;
      const isCompleted = visitedSteps.has(step.number) && !isActive;
      const isFirst = index === 0;

      return (
        <React.Fragment key={step.number}>
          {!isFirst && (
            <div
              className={`pjr-step-connector ${isCompleted ? 'completed' : ''}`}
            />
          )}
          <div
            className={`pjr-step ${isActive ? 'active' : ''} ${
              isCompleted ? 'completed' : ''
            }`}
            onClick={() => onStepClick(step.number)}
            title={step.label}
          >
            <div className="pjr-step-circle">
              {isCompleted ? (
                <FontAwesomeIcon icon={faCheck} style={{ fontSize: 12 }} />
              ) : (
                step.number
              )}
            </div>
            <span className="pjr-step-label">{step.shortLabel}</span>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Job Header Banner ─────────────────────────────────────────────────────────

const JobHeaderBanner: React.FC<{ job: Job }> = ({ job }) => {
  const chips: { label: string; value: string }[] = [
    { label: 'Customer', value: job.customer?.organizationName || '—' },
    { label: 'Rig', value: job.rig?.name || '—' },
    { label: 'Well', value: job.well || '—' },
    { label: 'Job #', value: job.jobNumber || '—' },
    {
      label: 'Supervisor',
      value: job.supervisor?.[0]
        ? `${job.supervisor[0].firstName} ${job.supervisor[0].lastName}`
        : '—'
    },
    { label: 'Stage', value: job.jobStage?.name || '—' }
  ];

  return (
    <div className="pjr-header-banner">
      {chips.map((chip, i) => (
        <React.Fragment key={chip.label}>
          {i > 0 && <div className="pjr-header-divider" />}
          <div className="pjr-header-chip">
            <span className="label">{chip.label}</span>
            <span className="value">{chip.value}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── Main Modal ────────────────────────────────────────────────────────────────

interface PostJobReportModalProps {
  show: boolean;
  onHide: () => void;
  job: Job;
}

const PostJobReportModal: React.FC<PostJobReportModalProps> = ({
  show,
  onHide,
  job
}) => {
  const { authUser } = useAccessToken();
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState(1);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([1]));
  const [reportData, setReportData] = useState<PostJobReport>(() =>
    createDefaultReport(job)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const pdfFileName = `HOC-PostJobReport-${job.jobNumber || getEntityId(job)}-${
    new Date().toISOString().split('T')[0]
  }.pdf`;

  // ── Load existing draft on open ────────────────────────────────────────────
  useQuery(
    ['postJobReport', getEntityId(job)],
    () => getActivityLogsByJobId(getEntityId(job) as string),
    {
      enabled: show && !!getEntityId(job),
      staleTime: 0,
      onSuccess: (data: any) => {
        const logs = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
        const reportLogs = logs.filter(
          (l: any) => l.activityType === 'post_job_report'
        );
        if (reportLogs.length > 0) {
          const latest = reportLogs[0];
          if (latest?.metadata) {
            setReportData({ ...createDefaultReport(job), ...latest.metadata });
            setIsSubmitted(latest.metadata.reportStatus === 'submitted');
            setLastSaved(new Date(latest.createdAt));
          }
        }
      },
      onError: () => setLoadError('Could not load existing report data.')
    }
  );

  // Reset on open
  useEffect(() => {
    if (show) {
      setCurrentStep(1);
      setVisitedSteps(new Set([1]));
    }
  }, [show]);

  // ── Report data updates ────────────────────────────────────────────────────
  const handleChange = useCallback((updates: Partial<PostJobReport>) => {
    setReportData(prev => ({ ...prev, ...updates }));
  }, []);

  // ── Save helpers ───────────────────────────────────────────────────────────
  const doSave = async (status: 'draft' | 'submitted') => {
    if (!getEntityId(job) || !authUser || !getEntityId(authUser)) {
      toast.error('Cannot save: missing job or user context');
      return false;
    }
    setIsSaving(true);
    try {
      const payload = { ...reportData, reportStatus: status };
      await CREATE_JOB_ACTIVITY_LOG({
        jobId: getEntityId(job) as string,
        activityType: 'post_job_report',
        description: `Post Job Report — ${
          status === 'submitted' ? 'Submitted' : 'Draft saved'
        } by ${authUser.firstName} ${authUser.lastName}`,
        metadata: payload
      });
      setLastSaved(new Date());
      setReportData(prev => ({ ...prev, reportStatus: status }));
      if (status === 'submitted') setIsSubmitted(true);
      queryClient.invalidateQueries(['postJobReport', getEntityId(job)]);
      return true;
    } catch {
      toast.error('Failed to save report. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    const ok = await doSave('draft');
    if (ok) toast.success('Draft saved successfully');
  };

  const handleSubmit = async () => {
    const ok = await doSave('submitted');
    if (ok) {
      toast.success('Report submitted successfully!');
    }
  };

  // ── Step navigation ────────────────────────────────────────────────────────
  const goToStep = (step: number) => {
    setCurrentStep(step);
    setVisitedSteps(prev => new Set([...prev, step]));
  };

  const handleNext = async () => {
    if (currentStep < 6) {
      await doSave('draft');
      goToStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) goToStep(currentStep - 1);
  };

  // ── Step content ───────────────────────────────────────────────────────────
  const stepProps = { reportData, onChange: handleChange, job };

  const stepContent = useMemo(() => {
    switch (currentStep) {
      case 1:
        return <Step1WellSetup {...stepProps} />;
      case 2:
        return <Step2DrillingParams {...stepProps} />;
      case 3:
        return <Step3ToolCasing {...stepProps} />;
      case 4:
        return <Step4BHA {...stepProps} />;
      case 5:
        return <Step5EngineeringLog {...stepProps} />;
      case 6:
        return <Step6ReviewExport {...stepProps} />;
      default:
        return null;
    }
  }, [currentStep, reportData]);

  const lastSavedText = lastSaved
    ? `Last saved: ${lastSaved.toLocaleTimeString()}`
    : 'Not yet saved';

  return (
    <Modal show={show} onHide={onHide} fullscreen className="pjr-modal">
      <Modal.Header closeButton className="py-2 px-3">
        <div className="d-flex align-items-center gap-3 w-100">
          <Modal.Title className="fs-6 fw-bold mb-0">
            Post Job Underreamer Report
          </Modal.Title>
          <span className="text-muted fs-10">HOC/FORMS/020</span>
          <span
            className={
              isSubmitted ? 'pjr-status-submitted' : 'pjr-status-draft'
            }
          >
            {isSubmitted ? 'Submitted' : 'Draft'}
          </span>
          <span className="ms-auto pjr-last-saved">{lastSavedText}</span>
        </div>
      </Modal.Header>

      {/* Job Header Banner */}
      <JobHeaderBanner job={job} />

      {/* Stepper */}
      <Stepper
        currentStep={currentStep}
        visitedSteps={visitedSteps}
        onStepClick={goToStep}
      />

      <Modal.Body className="overflow-auto p-4">
        {loadError && (
          <Alert
            variant="warning"
            onClose={() => setLoadError(null)}
            dismissible
            className="fs-9"
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {loadError}
          </Alert>
        )}

        {/* Step Header */}
        <div className="d-flex align-items-center mb-3">
          <div>
            <h5 className="mb-0 fw-bold">
              Step {currentStep}: {REPORT_STEPS[currentStep - 1].label}
            </h5>
            <p className="text-muted fs-9 mb-0">
              {currentStep === 6
                ? 'Review all entries, complete bit grading & comments, then export or submit.'
                : 'Fill in the fields below. Calculated values update automatically.'}
            </p>
          </div>
        </div>

        {stepContent}
      </Modal.Body>

      <Modal.Footer className="py-2 px-3 d-flex justify-content-between align-items-center">
        {/* Left: Exit + Back */}
        <div className="d-flex gap-2">
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => {
              if (
                window.confirm(
                  'Exit without saving? Any unsaved changes will be lost.'
                )
              ) {
                onHide();
              }
            }}
          >
            <FontAwesomeIcon icon={faRightFromBracket} className="me-1" />
            Exit
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleBack}
            disabled={currentStep === 1 || isSaving}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
            Back
          </Button>
        </div>

        {/* Center: Save Draft */}
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleSaveDraft}
            disabled={isSaving || isSubmitted}
          >
            {isSaving ? (
              <Spinner size="sm" animation="border" className="me-1" />
            ) : (
              <FontAwesomeIcon icon={faSave} className="me-1" />
            )}
            Save Draft
          </Button>

          {/* PDF Download — always available once data exists */}
          <PDFDownloadLink
            document={<PostJobReportPDF report={reportData} job={job} />}
            fileName={pdfFileName}
          >
            {({ loading }) => (
              <Button variant="outline-success" size="sm" disabled={loading}>
                <FontAwesomeIcon icon={faDownload} className="me-1" />
                {loading ? 'Preparing PDF…' : 'Export PDF'}
              </Button>
            )}
          </PDFDownloadLink>

          {/* Submit — only on step 6 and not already submitted */}
          {currentStep === 6 && !isSubmitted && (
            <Button
              variant="success"
              size="sm"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? (
                <Spinner size="sm" animation="border" className="me-1" />
              ) : (
                <FontAwesomeIcon icon={faCheck} className="me-1" />
              )}
              Submit Report
            </Button>
          )}
        </div>

        {/* Right: Next */}
        {currentStep < 6 ? (
          <Button
            variant="primary"
            size="sm"
            onClick={handleNext}
            disabled={isSaving}
          >
            {isSaving ? (
              <Spinner size="sm" animation="border" className="me-1" />
            ) : null}
            Next
            <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
          </Button>
        ) : (
          <div style={{ width: 80 }} />
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PostJobReportModal;
